import cloneDeep from "lodash/cloneDeep";
import compact from "lodash/compact";
import flatten from "lodash/flatten";
import { ECOption } from "PFCore/helpers/echarts_wrapper";
import calendarPng from "PFImages/calendar.png";
import filterPng from "PFImages/filter.png";
import fontFamilies from "PFTheme/tokens/fontFamilies";
import fontSizes from "PFTheme/tokens/fontSizes";
import { Color } from "PFTypes";
import { MatchesDistribution } from "PFTypes/matches_distribution";

export const AXIS_LABEL_MARGIN = 8;

// In API we have prefiltered and available_and_prefiltered range which is a sub range of the prefiltered
// range, but in the chart library I have to display them as two separate ranges so it looks fine
const getRangesMinMax = (
  counters: MatchesDistribution["counters"],
  dontModify: boolean,
  roundDownGap: (num: number) => number
): MatchesDistribution["counters"] => {
  if (dontModify) {
    return counters;
  }
  const newCounters = cloneDeep(counters);
  if (roundDownGap(counters.prefiltered.min) === roundDownGap(counters.prefilteredAndAvailable.min)) {
    if (counters.prefiltered.max > counters.prefilteredAndAvailable.max) {
      newCounters.prefiltered.min = counters.prefilteredAndAvailable.max;
    } else {
      newCounters.prefilteredAndAvailable.min = counters.prefiltered.max;
    }
  }
  if (roundDownGap(counters.prefiltered.max) === roundDownGap(counters.prefilteredAndAvailable.max)) {
    if (counters.prefiltered.min < counters.prefilteredAndAvailable.min) {
      newCounters.prefiltered.max = counters.prefilteredAndAvailable.min;
    } else {
      newCounters.prefilteredAndAvailable.max = counters.prefiltered.min;
    }
  }

  return newCounters;
};

const transformData = (
  data: MatchesDistribution["data"],
  min: number,
  max: number,
  xGap: number,
  roundDownGap: (num: number) => number
) => {
  // interpolating percentages so that we have a possibility to highlight between the bars
  // [10, 15, 20] -> [7.5, 10, 12.5, 15, 17.5, 20, 22.5]
  const _percentages: Record<string, number> = Object.entries(data).reduce((acc, [key, value]) => {
    if (value !== 0 || (parseInt(key) > min && parseInt(key) < max)) {
      const newKey = roundDownGap(parseInt(key)).toString();
      acc[newKey] = acc[newKey] !== undefined ? acc[newKey] + value : value;
    }
    return acc;
  }, {});
  const percentages = Object.keys(_percentages);

  const xAxisData = flatten(
    percentages.map((category, index) => {
      const matchPercentage = parseInt(category);
      return index === percentages.length - 1
        ? [matchPercentage - xGap / 2, matchPercentage, matchPercentage + xGap / 2]
        : [matchPercentage - xGap / 2, matchPercentage];
    })
  );

  // category takes value of the same index as it has so we need to set 0 as value to interpolated percentages
  const seriesData = flatten(
    Object.values(_percentages).map((value, index) =>
      index === percentages.length - 1 ? [0, value, 0] : [0, value]
    )
  );

  return { xAxisData, seriesData };
};

export const matchesChartConfig = (
  isActivityFilterEnabled: boolean,
  getColor: (color: Color) => string,
  distribiution?: MatchesDistribution
): ECOption | null => {
  if (!distribiution) {
    return null;
  }

  const xGap = distribiution.counters.matched.max - distribiution.counters.matched.min > 55 ? 10 : 5;
  const roundDownGap = (num: number) => Math.floor(num / xGap) * xGap;
  const getHighlightValueUp = (num: number) => ((Math.ceil(num / (xGap / 2)) * xGap) / 2).toString();
  const getHighlightValueDown = (num: number) => ((Math.floor(num / (xGap / 2)) * xGap) / 2).toString();

  const { xAxisData, seriesData } = transformData(
    distribiution.data,
    distribiution.counters.matched.min,
    distribiution.counters.matched.max,
    xGap,
    roundDownGap
  );

  const isSameRanges =
    isActivityFilterEnabled &&
    getHighlightValueDown(distribiution.counters.prefiltered.max) ===
      getHighlightValueDown(distribiution.counters.prefilteredAndAvailable.max) &&
    getHighlightValueDown(distribiution.counters.prefiltered.min) ===
      getHighlightValueDown(distribiution.counters.prefilteredAndAvailable.min);

  const {
    prefiltered: { min: prefilteredMin, max: prefilteredMax, total: prefilteredTotal },
    prefilteredAndAvailable: { min: availableMin, max: availableMax, total: availableTotal }
  } = getRangesMinMax(distribiution.counters, !isActivityFilterEnabled || isSameRanges, roundDownGap);

  const needsLabelOffset = !isSameRanges && Math.abs(prefilteredMin - availableMin) < xGap;

  return {
    xAxis: {
      data: xAxisData,
      containShape: false,
      axisLine: { lineStyle: { color: getColor("paletteBlue1") } },
      axisTick: { show: false },
      axisLabel: {
        fontFamily: fontFamilies.fontFamilyMulish,
        fontSize: 10,
        color: getColor("paletteBlue2"),
        interval: 0,
        // dont display interpolated categories labels (2.5/7.5...)
        formatter: (value: string) => (parseFloat(value) % xGap === 0 ? value : "")
      }
    },
    grid: {
      top: 16,
      bottom: 0,
      left: 0,
      right: 16,
      borderColor: getColor("paletteNeutral0"),
      containLabel: true
    },
    yAxis: {
      minInterval: 5,
      axisLine: { show: true, lineStyle: { color: getColor("paletteBlue1") } },
      axisLabel: {
        fontFamily: fontFamilies.fontFamilyMulish,
        fontSize: 10,
        color: getColor("paletteBlue2"),
        verticalAlign: "top",
        margin: AXIS_LABEL_MARGIN
      }
    },
    series: [
      {
        name: "Profiles",
        type: "bar",
        data: seriesData,
        barWidth: "130%",
        itemStyle: {
          color: "#436FB6"
        },
        emphasis: {
          disabled: true
        },
        cursor: "default",
        markArea: {
          silent: true,
          data: compact([
            !isSameRanges && [
              {
                xAxis:
                  prefilteredMin === availableMax
                    ? getHighlightValueUp(prefilteredMin)
                    : getHighlightValueDown(prefilteredMin),
                itemStyle: {
                  color: getColor("paletteNeutral0"),
                  opacity: 0.6
                },
                name: "Prefiltered",
                label: {
                  position: [needsLabelOffset && prefilteredMin >= availableMin ? 16 : 0, -16],
                  formatter: `{icon|} ${prefilteredTotal}`,
                  color: getColor("paletteBlue0"),
                  fontSize: fontSizes.fontSizeLabel,
                  richInheritPlainLabel: false,
                  rich: {
                    icon: {
                      backgroundColor: {
                        image: filterPng
                      },
                      height: 12
                    },
                    count: {
                      fontFamily: fontFamilies.fontFamilyMulish
                    }
                  }
                }
              },
              {
                xAxis:
                  prefilteredMax === availableMin
                    ? getHighlightValueDown(prefilteredMax)
                    : getHighlightValueDown(prefilteredMax)
              }
            ],
            isActivityFilterEnabled &&
              availableMax &&
              availableMin && [
                {
                  xAxis:
                    availableMin === prefilteredMax
                      ? getHighlightValueUp(availableMin)
                      : getHighlightValueDown(availableMin),
                  itemStyle: {
                    color: getColor("paletteNeutral3"),
                    opacity: 0.6
                  },
                  name: "Prefiltered and available",
                  label: {
                    position: [needsLabelOffset && availableMin > prefilteredMin ? 16 : 0, -16],
                    formatter: `{icon|} ${availableTotal}`,
                    color: getColor("paletteBlue0"),
                    fontSize: fontSizes.fontSizeLabel,
                    richInheritPlainLabel: false,
                    rich: {
                      icon: {
                        backgroundColor: {
                          image: calendarPng
                        },
                        height: 12
                      },
                      count: {
                        fontFamily: fontFamilies.fontFamilyMulish
                      }
                    }
                  }
                },
                {
                  xAxis:
                    availableMax === prefilteredMin
                      ? getHighlightValueDown(availableMax)
                      : getHighlightValueDown(availableMax)
                }
              ]
          ])
        }
      }
    ]
  };
};
